/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        this.mainProductForm = this.section.querySelector('.js-product');
        this.buyButtons = this.section.querySelector('buy-buttons');
        this.imageContainer = this.querySelector('.sticky-atc__image');
        this.bisBtn = this.querySelector('.sticky-atc__bis');
        this.variantContainer = this.querySelector('.sticky-atc__details__variant');
        this.variantTitle = this.querySelector('.sticky-atc__details__variant__title');
        this.variantTitle?.toggleAttribute('hidden', !this.section.querySelector('variant-picker'));

        this.throttledOnScroll = throttle(StickyAtc.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll);

        this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
        this.section.addEventListener('on:media-gallery:change', this.updateImage.bind(this));

        // ── PDP redesign móvil (Figma "FIJO ABAJO") ──
        this.isFigma = this.classList.contains('sticky-atc--figma');
        this.sizeSelect = this.querySelector('.sticky-atc__size');
        this.atcInView = false;

        // Visible siempre que el ATC inline NO esté en pantalla (desde arriba
        // y tras pasarlo); se oculta solo mientras el ATC inline se ve.
        if (this.buyButtons && 'IntersectionObserver' in window) {
          this.atcObserver = new IntersectionObserver((entries) => {
            this.atcInView = entries[0].isIntersecting;
            StickyAtc.handleScroll.call(this);
          }, { threshold: 0 });
          this.atcObserver.observe(this.buyButtons);
        }

        // El selector de talla del sticky cambia la variante en todo el PDP.
        if (this.sizeSelect) {
          this.sizeSelect.addEventListener('change', this.onSizeSelectChange.bind(this));
        }

        // Precio "· 12€" en el AÑADIR (figma).
        this.saLabel = this.querySelector('[data-sa-label]');
        this.saPrice = this.querySelector('[data-sa-price]');
        this.moneyFmt = this.dataset.saMoneyFormat || '';

        // Desktop figma: alinear el borde derecho con el bloque de producto.
        this.boundAlignRight = StickyAtc.alignRight.bind(this);
        window.addEventListener('resize', this.boundAlignRight);
        this.boundAlignRight();

        this.updateImage();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.throttledOnScroll);
        if (this.atcObserver) this.atcObserver.disconnect();
        if (this.boundAlignRight) window.removeEventListener('resize', this.boundAlignRight);
      }

      /**
       * Desktop figma: alinea el borde derecho del sticky con el de la columna
       * de producto (.product-column-right), no con --container-pad-x (que deja
       * el sticko ~17px corto). Se auto-ajusta a cualquier ancho.
       */
      static alignRight() {
        if (!this.isFigma) return;
        const col = this.section.querySelector('.product-column-right');
        if (!col || !window.matchMedia('(min-width: 768px)').matches) {
          this.style.removeProperty('inset-inline-end');
          return;
        }
        const right = col.getBoundingClientRect().right;
        this.style.setProperty('inset-inline-end', `${Math.max(0, Math.round(window.innerWidth - right))}px`, 'important');
      }


      /**
       * Handles submission of the product form.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        evt.preventDefault();

        const variantId = this.section.querySelector('product-form buy-buttons input[name="id"]').value;
        if (!variantId) {
          const target = this.section.querySelector('#product-info');
          if (target && target.offsetParent) {
            evt.preventDefault();
            theme.scrollToRevealElement(target);
          }
          return;
        }

        // Validate main form
        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductForm.form.reportValidity()) {
          evt.preventDefault();
          if (!customFormValid) {
            theme.scrollToRevealElement(this.mainProductForm);
          } else {
            const input = Array.from(this.mainProductForm.form.elements).find(
              (el) => !el.checkValidity()
            );
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        // Copy data
        this.form.querySelectorAll('[data-copied]').forEach((el) => el.remove());
        const formData = new FormData(this.mainProductForm.form);
        for (const p of formData) {
          if (!this.form.querySelector(`[name="${p[0]}"]`)) {
            const input = document.createElement('input');
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute('data-copied', '');
            this.form.append(input);
          }
        }
        super.handleSubmit(evt);
      }

      /**
       * Determine visibility after scroll.
       */
      static handleScroll() {
        const topOffset = document.querySelector('.pageheader--sticky') ? document.querySelector('.section-header').offsetHeight : 0;
        // Móvil rediseño: visible desde arriba y tras pasar el ATC inline (observer).
        // Desktop: la columna de producto es sticky (el ATC inline queda fijo al
        // bajar) -> el observer lo vería siempre visible; se mantiene la regla de
        // scroll (aparece cuando el ATC inline sube por encima del header).
        const figmaMobile = this.isFigma && window.matchMedia('(max-width: 749px)').matches;
        const out = figmaMobile
          ? this.atcInView === true
          : this.buyButtons.getBoundingClientRect().bottom > topOffset;
        this.classList.toggle('sticky-atc--out', out);

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
      }

      /**
       * Handle a change in variant on the page.
       * @param {Event} evt - Variant change event dispatched by variant-picker.
       */
      onVariantChange(evt) {
        const idInput = this.form?.querySelector('[name="id"]');

        if (evt.detail.variant) {
          this.variantTitle.textContent = evt.detail.variant.title;
          this.variantContainer.hidden = false;
          if (idInput) idInput.value = evt.detail.variant.id;
        } else {
          this.variantContainer.hidden = true;
          this.variantTitle.textContent = '';
          if (idInput) idInput.value = '';
        }

        // keep the sticky size <select> in sync with the page's variant
        if (this.sizeSelect && evt.detail.variant) {
          this.sizeSelect.value = evt.detail.variant.id;
        }

        this.updateImage();
        this.updateAddToCartButton(evt.detail.variant);
        this.setErrorMsgState();
      }

      /**
       * Sticky size <select> changed → drive the main variant picker so the
       * whole PDP (price, gallery, main form) updates. Single source of truth:
       * the change flows back via on:variant:change to re-sync this select.
       */
      onSizeSelectChange() {
        const opt = this.sizeSelect.options[this.sizeSelect.selectedIndex];
        if (!opt) return;
        const size = opt.getAttribute('data-opt');
        const picker = this.section.querySelector('variant-picker');
        if (!picker || size == null) return;

        // Redesign uses radio chips (input.js-option); match by value.
        let matched = null;
        picker.querySelectorAll('input.js-option').forEach((inp) => {
          if (!matched && inp.value === size) matched = inp;
        });
        if (matched) {
          matched.checked = true;
          matched.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        // Fallback: dropdown-style picker (custom-select).
        let ddMatch = null;
        picker.querySelectorAll('.custom-select__option').forEach((el) => {
          if (!ddMatch && (el.dataset.value === size || el.textContent.trim() === size)) ddMatch = el;
        });
        if (ddMatch) ddMatch.dispatchEvent(new MouseEvent('click', { bubbles: true }));
      }

      /**
       * Updates the image.
       */
      updateImage() {
        const mainImage = this.section.querySelector('.media-gallery .main-image .slider__item.is-active .product-media img');

        if (mainImage) {
          this.imageContainer.innerHTML = mainImage.outerHTML;
        } else {
          this.imageContainer.textContent = '';
        }
      }

      /**
       * Updates the "Add to Cart" button label and status.
       * @param {object} variant - Current variant object.
       */
      updateAddToCartButton(variant) {
        const hasInventoryTracking = variant && variant.inventory_management != null && variant.inventory_management !== '';
        const policy = variant && variant.inventory_policy;
        const qty = variant ? Number(variant.inventory_quantity) : NaN;
        const deniedSoldOut = hasInventoryTracking && policy === 'deny' && !Number.isNaN(qty) && qty <= 0;
        const variantAvailable = variant && variant.available && !deniedSoldOut;

        // Toggle the sold-out CTA swap: CSS hides the add-to-cart and reveals the
        // compact "Notify me" BIS trigger when the host has this class.
        this.classList.toggle('sticky-atc--sold-out', !variantAvailable);
        if (this.bisBtn && variant) {
          this.bisBtn.setAttribute('data-variant-id', variant.id);
        }

        if (!this.submitBtn) return;

        const unavailableStr = variant
          ? theme.strings.noStock : this.submitBtn.dataset.unavailableText;

        if (this.saPrice) {
          // figma: mantener la estructura "AÑADIR · 12€"; actualizar label + precio
          // (en agotado el ATC se oculta y sale el BIS, así que no se toca el texto).
          // OJO: evt.detail.variant llega SIN .price (undefined) -> antes daba
          // "NaN €". En su lugar ESPEJAMOS el precio principal (.price__current,
          // ya normalizado sin ,00), tras un rAF para que el picker lo actualice.
          if (variantAvailable) {
            if (this.saLabel) this.saLabel.textContent = this.submitBtn.dataset.addToCartText;
            const saPrice = this.saPrice;
            requestAnimationFrame(() => {
              const mp = document.querySelector('.pdp-figma-m .product-info__price .price__current');
              // formato del sticky = cc: sin ,00 y con el € pegado ("60€")
              if (mp && saPrice) saPrice.textContent = mp.textContent.trim().replace(/([.,])00(?!\d)/g, '').replace(/(\d)\s+€/, '$1€');
            });
          }
        } else {
          this.submitBtn.textContent = variantAvailable
            ? this.submitBtn.dataset.addToCartText
            : unavailableStr;
        }

        // Ensure the sticky CTA can't be submitted if the variant is not purchasable.
        this.submitBtn.disabled = !variantAvailable;
        if (!variantAvailable) {
          this.submitBtn.setAttribute('aria-disabled', 'true');
        } else {
          this.submitBtn.removeAttribute('aria-disabled');
        }
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}

